import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { Tag } from "primereact/tag";
import {
  useEquipmentsByFilter,
  useAllVehicleMileageLogs,
} from "presentation/hooks/use-query-equipments";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { useEquipmentTypeByFilterAll } from "presentation/hooks/use-query-equipment-type";
import { Equipment } from "domain/entities/equipment";
import Spinner from "presentation/components/Shared/Spinner";
import {
  filterEquipmentsByVehicleType,
  getVehicleEquipmentType,
} from "./equipment-utils";

const AdminVehicleMileageTab: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const selectedMonth = selectedDate.getMonth() + 1;
  const selectedYear = selectedDate.getFullYear();

  // Fetch all equipments, employees, types, and all mileage logs
  const { data: equipmentsResponse, isLoading: loadingEquipments } =
    useEquipmentsByFilter({ limit: 1000 });
  const { data: employeesResponse, isLoading: loadingEmployees } =
    useEmployeesByFilter({ limit: 200 });
  const { data: eqTypesResponse, isLoading: loadingTypes } =
    useEquipmentTypeByFilterAll({ limit: 100 } as any);
  const { data: allMileageLogsResponse, isLoading: loadingLogs } =
    useAllVehicleMileageLogs({ month: selectedMonth, year: selectedYear });

  if (loadingEquipments || loadingEmployees || loadingTypes || loadingLogs) {
    return <Spinner />;
  }

  // Find the Viatura EquipmentType ID
  const viaturaType = getVehicleEquipmentType(eqTypesResponse?.data);

  // Filter only vehicles
  const vehicles = filterEquipmentsByVehicleType(
    equipmentsResponse?.data?.data as Equipment[] | undefined,
    viaturaType?.id,
    true,
  );

  // Build grid data by matching vehicles with logs for the selected month/year
  const gridData = vehicles.map((vehicle: Equipment) => {
    const employee = employeesResponse?.data?.find(
      (e) => e.id === vehicle.employeeId,
    );

    // Find log for this vehicle, month, and year
    const log = allMileageLogsResponse?.data?.find(
      (l: any) =>
        l.equipmentId === vehicle.id &&
        l.month === selectedMonth &&
        l.year === selectedYear,
    );

    return {
      id: vehicle.id,
      brand: vehicle.brand,
      model: vehicle.model,
      registration: vehicle.serialOrCarRegistration,
      employeeName: employee?.name || "Não alocado",
      kms: log ? log.kms : null,
      createdAt: log ? log.createdAt : null,
      status: log ? "REGISTERED" : "PENDING",
    };
  });

  const employeeBodyTemplate = (rowData: any) => {
    return rowData.employeeName;
  };

  const dateBodyTemplate = (rowData: any) => {
    if (!rowData.createdAt) return "-";
    return new Date(rowData.createdAt).toLocaleDateString("pt-PT");
  };

  const kmsBodyTemplate = (rowData: any) => {
    if (rowData.kms === null) return "-";
    return `${rowData.kms.toLocaleString()} km`;
  };

  const statusBodyTemplate = (rowData: any) => {
    if (rowData.status === "REGISTERED") {
      return <Tag severity="success" value="Registado" />;
    }
    return <Tag severity="danger" value="Pendente" />;
  };

  return (
    <div>
      <div className="flex gap-4 align-items-center mb-4 mt-2">
        <div className="flex align-items-center gap-2">
          <label htmlFor="selectPeriod" style={{ fontWeight: "bold" }}>
            Período:
          </label>
          <Calendar
            id="selectPeriod"
            value={selectedDate}
            onChange={(e) => {
              if (e.value instanceof Date) {
                setSelectedDate(e.value);
              }
            }}
            view="month"
            dateFormat="mm/yy"
            showIcon
            style={{ width: "200px" }}
          />
        </div>
      </div>

      <DataTable
        value={gridData}
        paginator
        rows={10}
        emptyMessage="Nenhuma viatura encontrada."
        size="small"
      >
        <Column
          field="employeeName"
          header="Colaborador"
          body={employeeBodyTemplate}
          sortable
        />
        <Column field="brand" header="Marca" sortable />
        <Column field="model" header="Modelo" sortable />
        <Column field="registration" header="Matrícula" sortable />
        <Column
          field="kms"
          header="KMs Informados"
          body={kmsBodyTemplate}
          sortable
        />
        <Column
          field="createdAt"
          header="Data do Registo"
          body={dateBodyTemplate}
          sortable
        />
        <Column
          field="status"
          header="Estado"
          body={statusBodyTemplate}
          sortable
        />
      </DataTable>
    </div>
  );
};

export default AdminVehicleMileageTab;
